/**
 * Video Validation Utility
 * Comprehensive client-side video file validation
 */

// Allowed video extensions (whitelist)
const ALLOWED_EXTENSIONS = [
  ".mp4",
  ".webm",
  ".mov",
  ".avi",
  ".mkv",
  ".m4v",
  ".3gp",
  ".mpeg",
  ".mpg",
];

// Allowed MIME types (whitelist)
const ALLOWED_MIME_TYPES = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-matroska",
  "video/x-m4v",
  "video/3gpp",
  "video/mpeg",
];

// Default max file size: 200MB
const MAX_FILE_SIZE = 200 * 1024 * 1024;

// Default max duration: 120 seconds (2 minutes)
const MAX_DURATION = 120;

/**
 * Get video duration from file with proper memory cleanup
 * @param {File} file - Video file
 * @param {number} timeout - Timeout in ms (default: 10000)
 * @returns {Promise<number|null>} - Duration in seconds or null if error
 */
const getVideoDuration = (file, timeout = 10000) => {
  return new Promise((resolve) => {
    let video = null;
    let objectUrl = null;
    let timeoutId = null;
    let resolved = false;

    // Cleanup function to prevent memory leaks
    const cleanup = () => {
      if (resolved) return;
      resolved = true;

      // Clear timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Revoke object URL
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }

      // Clean up video element
      if (video) {
        video.onloadedmetadata = null;
        video.onerror = null;
        video.src = "";
        video.load(); // Reset video element
        video = null;
      }
    };

    // Set timeout to prevent hanging
    timeoutId = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeout);

    try {
      video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        const duration = video.duration;
        cleanup();
        resolve(duration);
      };

      video.onerror = () => {
        cleanup();
        resolve(null);
      };

      // Create object URL and store reference for cleanup
      objectUrl = URL.createObjectURL(file);
      video.src = objectUrl;
    } catch (error) {
      cleanup();
      resolve(null);
    }
  });
};

/**
 * Generate a safe filename for upload
 * @param {string} originalName - Original file name
 * @returns {string} - Sanitized filename with timestamp
 */
export const getSanitizedFileName = (originalName) => {
  const timestamp = Date.now();
  const extension = originalName.split(".").pop().toLowerCase();
  const baseName = originalName
    .substring(0, originalName.lastIndexOf("."))
    .replace(/[^a-zA-Z0-9]/g, "_")
    .substring(0, 50); // Limit base name length

  return `${baseName}_${timestamp}.${extension}`;
};

/**
 * Advanced Video Validation
 * @param {File} file - Video file to validate
 * @param {Object} options - Validation options
 * @param {number} options.maxSize - Max file size in bytes (default: 200MB)
 * @param {number} options.maxDuration - Max duration in seconds (default: 120s)
 * @param {boolean} options.checkDuration - Whether to check duration (default: true)
 * @returns {Promise<{isValid: boolean, error: string|null, sanitizedFileName: string|null}>}
 */
export const validateVideoFile = async (file, options = {}) => {
  const {
    maxSize = MAX_FILE_SIZE,
    maxDuration = MAX_DURATION,
    checkDuration = true,
  } = options;

  // 1. Check if file exists
  if (!file) {
    return {
      isValid: false,
      error: "No file selected",
      sanitizedFileName: null,
    };
  }

  // 2. Validate file extension
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf("."));

  if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
    return {
      isValid: false,
      error: `Invalid file type. Allowed formats: ${ALLOWED_EXTENSIONS.join(", ")}`,
      sanitizedFileName: null,
    };
  }

  // 3. Validate MIME type
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return {
      isValid: false,
      error: "Invalid video format. Please upload a valid video file.",
      sanitizedFileName: null,
    };
  }

  // 4. Validate file size
  if (file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    return {
      isValid: false,
      error: `Video file size must be less than ${maxSizeMB}MB`,
      sanitizedFileName: null,
    };
  }

  // 5. Validate duration (async)
  if (checkDuration) {
    const duration = await getVideoDuration(file);

    if (duration === null) {
      return {
        isValid: false,
        error: "Could not read video metadata. The file might be corrupted.",
        sanitizedFileName: null,
      };
    }

    if (duration > maxDuration) {
      const maxMinutes = Math.floor(maxDuration / 60);
      const maxSeconds = maxDuration % 60;
      const durationStr =
        maxMinutes > 0
          ? `${maxMinutes} minute${maxMinutes > 1 ? "s" : ""}${maxSeconds > 0 ? ` ${maxSeconds} seconds` : ""}`
          : `${maxSeconds} seconds`;

      return {
        isValid: false,
        error: `Video is too long. Maximum allowed duration is ${durationStr}.`,
        sanitizedFileName: null,
      };
    }
  }

  // 6. Generate sanitized filename
  const sanitizedFileName = getSanitizedFileName(file.name);

  // Log for debugging
  console.log("Video validation passed:", {
    originalName: file.name,
    sanitizedFileName,
    size: `${(file.size / (1024 * 1024)).toFixed(2)}MB`,
    type: file.type,
  });

  return {
    isValid: true,
    error: null,
    sanitizedFileName,
  };
};

/**
 * Quick sync validation (without duration check)
 * Use this for immediate feedback before async validation
 * @param {File} file - Video file to validate
 * @param {number} maxSize - Max file size in bytes
 * @returns {{isValid: boolean, error: string|null}}
 */
export const quickValidateVideo = (file, maxSize = MAX_FILE_SIZE) => {
  if (!file) {
    return { isValid: false, error: "No file selected" };
  }

  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf("."));

  if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
    return {
      isValid: false,
      error: `Invalid file type. Allowed formats: ${ALLOWED_EXTENSIONS.join(", ")}`,
    };
  }

  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return {
      isValid: false,
      error: "Invalid video format. Please upload a valid video file.",
    };
  }

  if (file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    return {
      isValid: false,
      error: `Video file size must be less than ${maxSizeMB}MB`,
    };
  }

  return { isValid: true, error: null };
};

// Export constants for external use
export { ALLOWED_EXTENSIONS, ALLOWED_MIME_TYPES, MAX_FILE_SIZE, MAX_DURATION };
